/**
 * La venta que se acaba de cerrar, y qué hacer con ella.
 *
 * POR QUE OCUPA EL TICKET VACIO Y NO ES UN TOAST
 * ----------------------------------------------
 * Mandar el remito no pasa en el instante de confirmar: pasa medio minuto
 * despues, mientras se guarda el equipo en la bolsa y el cliente cierra la
 * billetera. Un toast ya se fue para entonces. Este panel se queda hasta que
 * empiece la proxima venta, y ocupa un espacio que igual estaba vacio.
 *
 * Los avisos que antes iban en el toast —el saldo pendiente, el "vendiste sin
 * stock"— viven aca, donde se pueden leer con calma.
 *
 * LA APERTURA TIENE QUE SER SINCRONICA
 * ------------------------------------
 * Safari en iPhone bloquea un `window.open` que no esta dentro del gesto
 * original del usuario. Por eso el token viene en la respuesta de la venta y el
 * telefono del cliente se pide APENAS se abre el panel, no al tocar el boton:
 * cuando la persona toca, ya no queda ningun `await` en el medio.
 *
 * Diseño completo: docs/specs/2026-08-22-remito-por-whatsapp.md
 */
import { useEffect, useMemo, useState } from "react";
import { Check, Copy, MessageCircle, Wrench, X } from "lucide-react";
import type { Moneda } from "@tora/shared";

import { Importe } from "@/components/money/importe";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Codigo } from "@/components/ui/codigo";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { useActualizarCliente, useCliente } from "@/lib/queries";
import {
  armarMensajeRemito,
  linkWhatsapp,
  normalizarTelefonoAr,
  urlRemito,
  type ItemRemito,
  type LineaPagoRemito,
} from "@/lib/remito";
import { cn } from "@/lib/utils";

export interface VentaCerrada {
  numero: string;
  total: number;
  pagado: number;
  pendiente: number;
  moneda: Moneda;
  /**
   * La moneda de la cuenta del cliente y el pendiente en ella, cuando no es la
   * de la venta: una venta en dolares a una cuenta fija en pesos queda
   * congelada en pesos, y el mensaje tiene que decirlo como lo dice el remito.
   */
  monedaCuenta?: Moneda | null;
  pendienteCuenta?: number | null;
  /** `null` cuando la venta se emitio antes de que existieran los tokens. */
  token: string | null;
  items: ReadonlyArray<ItemRemito>;
  /** Cómo se pagó: los medios y el equipo que entró en parte de pago. */
  pagos: ReadonlyArray<LineaPagoRemito>;
  clienteId: number | null;
  clienteNombre: string | null;
  /** Textos ya armados: saldo pendiente, stock en negativo. */
  avisos: ReadonlyArray<string>;
  /** El usado que entro en parte de pago, si lo hubo. */
  entrega?: { producto_id: number; nombre: string } | null;
  /** Lo que se pago con el saldo a favor del cliente (0121_8). */
  saldoAFavor?: number;
}

export interface PanelVentaCerradaProps {
  venta: VentaCerrada;
  negocio: string;
  onCerrar: () => void;
  /** Mandar el usado recien recibido al taller. Sin esto el boton no aparece. */
  onReacondicionar?: (productoId: number) => void;
}

/*
 * En produccion la pantalla y el remito los sirve el MISMO proceso, asi que el
 * origen del navegador es la base correcta y no hay nada que configurar. La
 * variable existe para el dia que el remito viva en un dominio propio
 * (manzanamordida.com.ar) mientras la app sigue en otro lado.
 */
const BASE_PUBLICA = import.meta.env.VITE_URL_PUBLICA ?? window.location.origin;

export function PanelVentaCerrada({
  venta,
  negocio,
  onCerrar,
  onReacondicionar,
}: PanelVentaCerradaProps): JSX.Element {
  const [pidiendoNumero, setPidiendoNumero] = useState(false);
  const [copiado, setCopiado] = useState(false);
  /** Se llena solo si `window.open` fue bloqueado: entonces se muestra el link. */
  const [linkBloqueado, setLinkBloqueado] = useState<string | null>(null);

  // Se pide APENAS se monta el panel, no al tocar el boton: cuando la persona
  // toque, el telefono ya tiene que estar en memoria.
  const ficha = useCliente(venta.clienteId ?? undefined);
  const telefonoGuardado = ficha.data?.cliente?.telefono ?? null;

  const url = venta.token ? urlRemito(BASE_PUBLICA, venta.token) : null;

  const mensaje = useMemo(
    () =>
      url
        ? armarMensajeRemito({
            numero: venta.numero,
            fecha: new Date(),
            moneda: venta.moneda,
            total: venta.total,
            pagado: venta.pagado,
            pendiente: venta.pendiente,
            monedaCuenta: venta.monedaCuenta ?? null,
            pendienteCuenta: venta.pendienteCuenta ?? null,
            saldoAFavor: venta.saldoAFavor ?? 0,
            items: venta.items,
            pagos: venta.pagos,
            clienteNombre: venta.clienteNombre,
            negocio,
            url,
          })
        : "",
    [url, venta, negocio],
  );

  function abrirChat(e164: string): void {
    const destino = linkWhatsapp(e164, mensaje);
    // Algunos navegadores embebidos (el de Instagram, el de Gmail) bloquean la
    // apertura igual. Si no se abre, se muestra el link en vez de dejar a quien
    // vende mirando una pantalla que no hizo nada.
    const v = window.open(destino, "_blank", "noopener,noreferrer");
    if (!v) setLinkBloqueado(destino);
  }

  function enviar(): void {
    const t = normalizarTelefonoAr(telefonoGuardado);
    if (t && t.confianza === "alta") {
      abrirChat(t.e164);
      return;
    }
    setPidiendoNumero(true);
  }

  async function copiar(): Promise<void> {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopiado(true);
    } catch {
      setLinkBloqueado(url);
    }
  }

  useEffect(() => {
    if (!copiado) return;
    const t = setTimeout(() => setCopiado(false), 2000);
    return () => clearTimeout(t);
  }, [copiado]);

  const nombreCorto = venta.clienteNombre?.trim().split(/\s+/)[0];
  const etiqueta =
    nombreCorto && normalizarTelefonoAr(telefonoGuardado)?.confianza === "alta"
      ? `Enviar a ${nombreCorto}`
      : "Enviar por WhatsApp";
  const entrega = venta.entrega;

  return (
    // El tinte de exito va al contenedor; el importe adentro sigue en el color
    // de SU moneda (MASTER §3.2).
    <Card
      as="section"
      tono="success"
      padding="normal"
      data-testid="venta-cerrada"
      className="flex flex-col gap-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <TituloSeccion nivel="etiqueta">Venta cerrada</TituloSeccion>
          <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <Codigo className="text-sm font-semibold">{venta.numero}</Codigo>
            <span className="text-muted-foreground">·</span>
            <Importe monto={venta.total} moneda={venta.moneda} tamano="xl" />
          </div>
        </div>

        <IconButton icono={X} aria-label="Cerrar" className="shrink-0" onClick={onCerrar} />
      </div>

      {venta.avisos.length > 0 ? (
        <Aviso tono="warning">
          <ul className="flex flex-col gap-1">
            {venta.avisos.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </Aviso>
      ) : null}

      {entrega && onReacondicionar ? (
        <Card
          variant="muted"
          padding="compacto"
          className="flex flex-wrap items-center gap-2 text-xs"
        >
          <span className="min-w-0 flex-1 text-muted-foreground">
            Entró {entrega.nombre} en parte de pago.
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onReacondicionar(entrega.producto_id)}
          >
            <Wrench aria-hidden="true" />
            Mandar a reacondicionar
          </Button>
        </Card>
      ) : null}

      {url ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" onClick={enviar}>
            <MessageCircle aria-hidden="true" />
            {etiqueta}
          </Button>

          <Button type="button" variant="outline" onClick={() => void copiar()}>
            {copiado ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
            {copiado ? "Copiado" : "Copiar link"}
          </Button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          Esta venta se emitió antes de que existieran los remitos compartibles, así que no tiene
          link.
        </p>
      )}

      {linkBloqueado ? (
        <Aviso tono="warning" titulo="El navegador no dejó abrir WhatsApp">
          <span className="break-all text-muted-foreground">{linkBloqueado}</span>
        </Aviso>
      ) : null}

      <DialogNumero
        abierto={pidiendoNumero}
        onCerrar={() => setPidiendoNumero(false)}
        clienteId={venta.clienteId}
        clienteNombre={nombreCorto ?? null}
        inicial={telefonoGuardado ?? ""}
        onEnviar={(e164) => {
          setPidiendoNumero(false);
          abrirChat(e164);
        }}
      />
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ¿A qué número?
// ─────────────────────────────────────────────────────────────────────────────

function DialogNumero({
  abierto,
  onCerrar,
  clienteId,
  clienteNombre,
  inicial,
  onEnviar,
}: {
  abierto: boolean;
  onCerrar: () => void;
  clienteId: number | null;
  clienteNombre: string | null;
  inicial: string;
  onEnviar: (e164: string) => void;
}): JSX.Element {
  const [texto, setTexto] = useState(inicial);
  const [guardar, setGuardar] = useState(true);
  const actualizar = useActualizarCliente();

  useEffect(() => {
    if (abierto) setTexto(inicial);
  }, [abierto, inicial]);

  const t = normalizarTelefonoAr(texto);

  return (
    <Dialog open={abierto} onOpenChange={(v) => !v && onCerrar()}>
      <DialogContent tamano="sm">
        <DialogHeader>
          <DialogTitle>¿A qué número?</DialogTitle>
          <DialogDescription>
            {clienteId === null
              ? "Es solo para mandar este remito. No se da de alta ningún cliente."
              : "Todavía no tiene teléfono cargado."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="remito-tel">Teléfono</Label>
          <Input
            id="remito-tel"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            autoFocus
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="0376 15 412-3456"
          />

          {/*
            Que la persona VEA el numero antes de abrir el chat es la unica
            defensa real contra mandarle el remito de un cliente a un
            desconocido por un digito de mas. Ninguna heuristica reemplaza esto.
          */}
          {t ? (
            <p
              className={cn(
                "text-xs tabular-nums",
                t.confianza === "alta" ? "text-muted-foreground" : "text-warning",
              )}
            >
              {t.confianza === "alta"
                ? `Se abre el chat de ${t.legible}`
                : `Se abre el chat de ${t.legible} — revisalo, no parece un número argentino completo.`}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">Escribí el número como lo tengas.</p>
          )}
        </div>

        {clienteId !== null ? (
          <Checkbox
            checked={guardar}
            onChange={(e) => setGuardar(e.target.checked)}
            label={`Guardarlo en la ficha${clienteNombre ? ` de ${clienteNombre}` : ""}`}
          />
        ) : null}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            type="button"
            disabled={t === null}
            onClick={() => {
              if (!t) return;
              // El guardado va SIN await: si se esperara la respuesta, la
              // apertura de WhatsApp saldria del gesto del usuario y iOS la
              // bloquearia. Que el telefono se guarde o no es secundario;
              // mandar el remito es lo que la persona pidió.
              if (clienteId !== null && guardar) {
                actualizar.mutate({ id: clienteId, cambios: { telefono: texto.trim() } });
              }
              onEnviar(t.e164);
            }}
          >
            Enviar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
